import React, { Suspense, lazy } from 'react';
import RouteAccessGuard from 'access/RouteAccessGuard';
import Loader from 'components/Elements/Loader/Loader';
import { MainLayout } from 'components/Layout/MainLayout';
import { Outlet } from 'react-router-dom';
import UserSessionProvider from 'providers/UserSessionProvider';
import TableProviderMyTxt from 'providers/TableProviderMyTxt';
import { SclaApplicationRoutes } from 'features/scla/routes';
import PostLoginOrDefaultRedirect from 'features/auth/components/PostLoginOrDefaultRedirect';
import LegacySclaApplicationRedirect from 'routes/LegacySclaApplicationRedirect';

const HomeRoutes = lazy(() => import('features/home/routes'));
const MothershipAdminRoutes = lazy(
  () => import('features/mothership-admin/routes'),
);
const LegacyMothershipRedirect = lazy(
  () => import('features/mothership-admin/LegacyMothershipRedirect'),
);
const SealsRoutes = lazy(() => import('features/seals/routes'));
const ProfileRoutes = lazy(() => import('features/profile/routes'));
const TxtLabsApprenticeshipRoutes = lazy(
  () => import('features/txt-labs-apprenticeship/routes'),
);
const SclaTeamRoster = lazy(
  () => import('features/scla/routes/SclaTeamRoster'),
);
// WIP features — restore when ready
// const SignedFormsRoutes = lazy(() => import('features/signed-forms/routes'));
const TxtTrainingRoutes = lazy(() => import('features/txt-training/routes'));
const OfficeCheckInRoutes = lazy(() => import('features/office-check-in/routes'));
const PublicSignForm = lazy(() => import('features/signed-forms/routes/PublicSignForm'));
const SclaOrientationFormsList = lazy(
  () => import('features/scla-orientation-forms-public/routes/SclaOrientationFormsList'),
);
/**
 * Renders the routes. A loader is shown if user data is being fetched.
 * A loader is shown for routes that are lazy loaded
 * @returns
 */
function App() {
  return (
    <UserSessionProvider>
      {({ user }) => (
        <TableProviderMyTxt>
          <MainLayout>
            <Suspense
              fallback={
                <Loader theme="light" isVisible fullscreen={false} />
              }
            >
              <Outlet context={{ user }} />
            </Suspense>
          </MainLayout>
        </TableProviderMyTxt>
      )}
    </UserSessionProvider>
  );
}

const protectedRoutes = [
  {
    path: '/scla-orientation-forms',
    element: (
      <Suspense fallback={<Loader theme="dark" isVisible fullscreen />}>
        <SclaOrientationFormsList />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: 'sign/:token',
        element: (
          <Suspense fallback={<Loader theme="light" isVisible fullscreen={false} />}>
            <PublicSignForm />
          </Suspense>
        ),
      },
      {
        path: 'home/*',
        element: (
          <RouteAccessGuard ruleId="home">
            <HomeRoutes />
          </RouteAccessGuard>
        ),
      },
      {
        path: 'programs/seals/*',
        element: (
          <RouteAccessGuard ruleId="seals">
            <SealsRoutes />
          </RouteAccessGuard>
        ),
      },
      {
        path: 'scla-application/*',
        element: (
          <RouteAccessGuard ruleId="scla-application">
            <SclaApplicationRoutes />
          </RouteAccessGuard>
        ),
      },
      {
        path: 'application/*',
        element: <LegacySclaApplicationRedirect />,
      },
      {
        path: 'txt-labs/apprenticeship/*',
        element: (
          <RouteAccessGuard ruleId="txt-labs-apprenticeship">
            <TxtLabsApprenticeshipRoutes />
          </RouteAccessGuard>
        ),
      },
      // WIP — signed forms
      // {
      //   path: 'forms/*',
      //   element: (
      //     <RouteAccessGuard ruleId="signed-forms">
      //       <SignedFormsRoutes />
      //     </RouteAccessGuard>
      //   ),
      // },
      {
        path: 'txt-training/*',
        element: (
          <RouteAccessGuard ruleId="txt-training">
            <TxtTrainingRoutes />
          </RouteAccessGuard>
        ),
      },
      {
        path: 'office/*',
        element: (
          <RouteAccessGuard ruleId="office-check-in">
            <OfficeCheckInRoutes />
          </RouteAccessGuard>
        ),
      },
      {
        path: 'profile/*',
        element: (
          <RouteAccessGuard ruleId="profile">
            <ProfileRoutes />
          </RouteAccessGuard>
        ),
      },
      {
        path: 'scla/team',
        element: (
          <RouteAccessGuard ruleId="scla-team-roster">
            <Suspense
              fallback={<Loader theme="light" isVisible fullscreen={false} />}
            >
              <SclaTeamRoster />
            </Suspense>
          </RouteAccessGuard>
        ),
      },
      // SCLA → Mobile Applications (Rosie-aligned). App selected via ?app=
      {
        path: 'scla/mobile-applications/*',
        element: <MothershipAdminRoutes />,
      },
      // Legacy per-app mothership admin URLs
      {
        path: 'admin/mothership/:appSlug',
        element: (
          <Suspense
            fallback={<Loader theme="light" isVisible fullscreen={false} />}
          >
            <LegacyMothershipRedirect />
          </Suspense>
        ),
      },
      {
        path: 'admin/mothership/*',
        element: (
          <Suspense
            fallback={<Loader theme="light" isVisible fullscreen={false} />}
          >
            <LegacyMothershipRedirect />
          </Suspense>
        ),
      },
      {
        path: '',
        element: <PostLoginOrDefaultRedirect defaultPath="/home" />,
      },
    ],
  },
  // Also catches auth pages (/sign-up, /set-password, ...) right after the user
  // authenticates there, so a saved resource link still wins over the default.
  {
    path: '',
    element: <PostLoginOrDefaultRedirect defaultPath="/home" />,
  },
  {
    path: '*',
    element: <PostLoginOrDefaultRedirect defaultPath="/home" />,
  },
];

export default protectedRoutes;
